import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, KeyRound, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { calFetch, fmtGreg, fmtHebrew } from "@/lib/calApi";
import { getAdminPassword, setAdminPassword } from "@/lib/adminApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PersonForm } from "@/components/calendar/admin/PersonForm";
import { EventForm } from "@/components/calendar/admin/EventForm";
import { PushTab } from "@/components/calendar/admin/PushTab";

const TABS = [["people", "Personas"], ["events", "Fechas especiales"], ["proposals", "Correcciones"], ["accounts", "Cuentas"], ["push", "Avisos"]];
const rowCls = "flex items-center gap-3 px-4 py-3";
const iconBtn = "p-2 rounded-full hover:bg-paper text-cocoa-soft hover:text-cocoa transition-colors";

function Gate({ onOk }) {
  const [pw, setPw] = useState("");
  const go = async (e) => {
    e.preventDefault();
    try { await calFetch("/cal/admin/data", { method: "POST", admin: pw }); setAdminPassword(pw); onOk(pw); } catch (err) { toast.error(err.message); }
  };
  return (
    <form onSubmit={go} className="max-w-sm mx-auto rounded-3xl bg-cream border border-line p-6 mt-10">
      <h2 className="font-display italic text-cocoa text-2xl mb-3">Clave de administrador</h2>
      <input type="password" data-testid="cal-admin-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="tu clave secreta"
        className="w-full px-4 py-3 rounded-xl bg-paper border border-line text-cocoa mb-3" />
      <button data-testid="cal-admin-login" className="w-full rounded-full bg-night text-white py-3 font-semibold">Entrar</button>
    </form>
  );
}

function PeopleTab({ data, registered, onEdit, onDelete, onNew }) {
  const [q, setQ] = useState("");
  const list = data.people.filter((p) => `${p.name} ${p.nickname} ${p.branch}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-cocoa-soft" />
          <input data-testid="cal-admin-people-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Buscar entre ${data.people.length} personas…`} className="w-full pl-9 pr-3 py-2.5 rounded-full bg-paper border border-line text-sm" /></div>
        <button data-testid="cal-admin-new-person" onClick={onNew} className="inline-flex items-center gap-1 rounded-full bg-night text-white px-4 text-sm font-semibold"><Plus className="w-4 h-4" /> Persona</button>
      </div>
      <div className="rounded-3xl bg-cream border border-line divide-y divide-line/70">
        {list.map((p) => (
          <div key={p.id} className={rowCls} data-testid={`cal-admin-person-${p.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-cocoa truncate">{p.name}{p.nickname && <span className="font-normal text-cocoa-soft"> · {p.nickname}</span>}
                {registered.includes(p.id) && <span className="ml-2 text-[10px] rounded-full bg-dusk/10 text-dusk px-2 py-0.5">con clave</span>}</p>
              <p className="text-xs text-cocoa-soft truncate">{fmtGreg(p.gdate)}{p.gdate_auto ? " (calculada)" : ""} · {fmtHebrew(p.hdate)}{p.hdate_auto ? " (calculada)" : ""} · {p.branch}{p.status ? ` · ${p.status}` : ""}</p>
            </div>
            <button aria-label="Editar" data-testid={`cal-admin-edit-${p.id}`} onClick={() => onEdit(p)} className={iconBtn}><Pencil className="w-4 h-4" /></button>
            <button aria-label="Borrar" data-testid={`cal-admin-delete-${p.id}`} onClick={() => onDelete(p)} className={iconBtn}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </>
  );
}

function EventsTab({ data, onEdit, onDelete, onNew }) {
  return (
    <>
      <button data-testid="cal-admin-new-event" onClick={onNew} className="mb-3 inline-flex items-center gap-1 rounded-full bg-night text-white px-4 py-2.5 text-sm font-semibold"><Plus className="w-4 h-4" /> Fecha especial</button>
      <div className="rounded-3xl bg-cream border border-line divide-y divide-line/70">
        {(data.events || []).map((ev) => (
          <div key={ev.id} className={rowCls} data-testid={`cal-admin-event-${ev.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-cocoa truncate">{ev.title}</p>
              <p className="text-xs text-cocoa-soft">{ev.type} · {ev.gdate ? fmtGreg(ev.gdate) : fmtHebrew(ev.hdate)} · {ev.calendar}</p>
            </div>
            <button aria-label="Editar" onClick={() => onEdit(ev)} className={iconBtn}><Pencil className="w-4 h-4" /></button>
            <button aria-label="Borrar" data-testid={`cal-admin-delete-event-${ev.id}`} onClick={() => onDelete(ev)} className={iconBtn}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {!(data.events || []).length && <p className="p-4 text-sm italic text-cocoa-soft">Aún no hay fechas especiales.</p>}
      </div>
    </>
  );
}

function ProposalsTab({ data, act }) {
  const list = data.proposals || [];
  if (!list.length) return <p className="text-sm italic text-cocoa-soft">No hay correcciones pendientes.</p>;
  return (
    <div className="space-y-3">
      {list.map((pr) => (
        <div key={pr.id} data-testid={`cal-admin-proposal-${pr.id}`} className="rounded-3xl bg-cream border border-line p-4">
          <p className="text-sm font-semibold text-cocoa">{pr.person_name}</p>
          {pr.changes.gdate && <p className="text-sm text-cocoa-soft">Gregoriana → {fmtGreg(pr.changes.gdate)}</p>}
          {pr.changes.hdate && <p className="text-sm text-cocoa-soft">Hebrea → {fmtHebrew(pr.changes.hdate)}</p>}
          {pr.note && <p className="text-sm italic text-cocoa-soft mt-1">“{pr.note}”</p>}
          <div className="flex gap-2 mt-3">
            <button data-testid={`cal-admin-approve-${pr.id}`} onClick={() => act(pr.id, "approve")} className="rounded-full bg-night text-white px-4 py-2 text-sm font-semibold">Aprobar</button>
            <button data-testid={`cal-admin-reject-${pr.id}`} onClick={() => act(pr.id, "reject")} className="rounded-full border border-line px-4 py-2 text-sm text-cocoa">Rechazar</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AccountsTab({ data, registered, reset }) {
  const list = data.people.filter((p) => registered.includes(p.id));
  return (
    <div className="rounded-3xl bg-cream border border-line divide-y divide-line/70">
      <p className="px-4 py-3 text-xs text-cocoa-soft">{list.length} de {data.people.filter((p) => !p.is_pet).length} ya crearon su clave.</p>
      {list.map((p) => (
        <div key={p.id} className={rowCls}>
          <p className="flex-1 text-sm text-cocoa">{p.name}</p>
          <button data-testid={`cal-admin-reset-${p.id}`} onClick={() => reset(p)} className="inline-flex items-center gap-1 text-xs rounded-full border border-line px-3 py-1.5 text-cocoa"><KeyRound className="w-3.5 h-3.5" /> Resetear clave</button>
        </div>
      ))}
    </div>
  );
}

export default function CalAdmin() {
  const [pw, setPw] = useState(getAdminPassword());
  const [needPw, setNeedPw] = useState(false);
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [tab, setTab] = useState("people");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const call = useCallback((path, body) => calFetch(path, { method: "POST", body, admin: pw }), [pw]);
  const load = useCallback(() => call("/cal/admin/data").then((r) => { setState(r); setNeedPw(false); })
    .catch((e) => { if (e.status === 401) { setNeedPw(true); if (pw) toast.error(e.message); } else toast.error(e.message); }), [call, pw]);
  useEffect(() => { load(); }, [load]);
  const branches = useMemo(() => [...new Set((state?.data.people || []).map((p) => p.branch).filter(Boolean))], [state]);

  const run = async (path, body, msg) => {
    setBusy(true);
    try { await call(path, body); toast.success(msg); setEditing(null); await load(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const confirmRun = (q, path, body, msg) => { if (window.confirm(q)) run(path, body, msg); };

  return (
    <div className="min-h-screen paper-texture iphone-safe-top pb-16">
      <header className="sticky top-0 z-30 bg-paper/90 backdrop-blur-xl border-b border-line">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center gap-3">
          <button type="button" onClick={() => (window.history.length > 1 ? nav(-1) : nav("/calendario"))} data-testid="cal-admin-back" aria-label="Volver" className="p-2 rounded-full bg-cream border border-line"><ChevronLeft className="w-5 h-5 text-cocoa" /></button>
          <div><p className="eyebrow text-[10px]">Panel privado</p><h1 className="font-display italic text-cocoa text-2xl leading-tight">Admin del Calendario</h1></div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 pt-6">
        {needPw ? <Gate onOk={setPw} /> : !state ? <div className="h-40 rounded-3xl bg-cream animate-pulse" /> : (
          <>
            <div className="flex flex-wrap gap-1.5 mb-5">
              {TABS.map(([k, l]) => (
                <button key={k} data-testid={`cal-admin-tab-${k}`} onClick={() => setTab(k)}
                  className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold border ${tab === k ? "bg-night text-white border-night" : "bg-cream text-cocoa border-line"}`}>
                  {l}{k === "proposals" && state.data.proposals?.length ? ` (${state.data.proposals.length})` : ""}
                </button>
              ))}
            </div>
            {tab === "people" && <PeopleTab data={state.data} registered={state.registered} onNew={() => setEditing({ kind: "person" })} onEdit={(p) => setEditing({ kind: "person", item: p })}
              onDelete={(p) => confirmRun(`¿Borrar a ${p.name} del calendario?`, "/cal/admin/delete-person", { id: p.id }, "Persona borrada")} />}
            {tab === "events" && <EventsTab data={state.data} onNew={() => setEditing({ kind: "event" })} onEdit={(ev) => setEditing({ kind: "event", item: ev })}
              onDelete={(ev) => confirmRun(`¿Borrar "${ev.title}"?`, "/cal/admin/delete-event", { id: ev.id }, "Fecha borrada")} />}
            {tab === "proposals" && <ProposalsTab data={state.data} act={(id, action) => run("/cal/admin/proposal", { id, action }, action === "approve" ? "Corrección aprobada" : "Corrección rechazada")} />}
            {tab === "push" && <PushTab call={call} />}
            {tab === "accounts" && <AccountsTab data={state.data} registered={state.registered}
              reset={(p) => confirmRun(`¿Resetear la clave de ${p.name}? Tendrá que crearla de nuevo.`, "/cal/admin/reset-password", { person_id: p.id }, "Clave reseteada")} />}
          </>
        )}
      </main>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-paper">
          <DialogHeader><DialogTitle className="font-display italic text-cocoa text-2xl">
            {editing?.kind === "event" ? (editing.item ? "Editar fecha" : "Nueva fecha especial") : (editing?.item ? "Editar persona" : "Nueva persona")}
          </DialogTitle></DialogHeader>
          {editing?.kind === "person" && <PersonForm initial={editing.item} branches={branches} busy={busy} onCancel={() => setEditing(null)}
            onSave={(person) => run("/cal/admin/save-person", { person }, "Persona guardada")} />}
          {editing?.kind === "event" && <EventForm initial={editing.item} people={state?.data.people || []} busy={busy} onCancel={() => setEditing(null)}
            onSave={(event) => run("/cal/admin/save-event", { event }, "Fecha guardada")} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
